/**
 * Skills adapter — points AuiFiles at the dedicated
 * `/api/v1/skills/{skillId}/files` endpoints.
 *
 * Differs from the VFS adapter in three ways the methods below absorb:
 *  1. No `fileId` — entries are synthesized from `SkillFileRef` via the store's
 *     `refToEntry` (path is the row key).
 *  2. Inline base64 upload (one POST), not the 3-step presigned VFS flow.
 *  3. No WebSocket events — an optimistic module store (`skill-files-store`)
 *     stands in for the event bus so uploads/deletes reflect immediately. Each
 *     mutation hits the REST endpoint, then patches the store.
 *
 * Folders are client-side virtual (no mkdir endpoint), reusing the same
 * `virtual-folders` module the VFS adapter uses.
 */

import { useCallback, useEffect, useState } from 'react';
import { skillFilesApi } from '@/services/api';
import { joinVfsPath } from '@/hooks/useVfsUpload';
import type { VfsUploadJob } from '@/hooks/useVfsUpload';
import type { VfsEntry } from '@/types/vfs';
import type {
  FilesBackend,
  FilesEntry,
  UseDeleteResult,
  UseEntriesResult,
  UseFoldersResult,
  UseUploadResult,
} from './types';
import {
  getSkillFilesSnapshot,
  moveSkillFile,
  refToEntry,
  removeSkillFile,
  removeSkillFilePrefix,
  setAllSkillFiles,
  subscribeSkillFiles,
  upsertSkillFile,
} from './skill-files-store';
import {
  addVirtualFolder,
  getVirtualFolders,
  pruneRealisedFolders,
  subscribeVirtualFolders,
} from './virtual-folders';

/** Upstream per-file cap (10 MB). Mirrored client-side for a clear error
 *  instead of a generic 400 from the backend. */
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** Read a File's body as base64 (no `data:` prefix). Binary-safe. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function createSkillsBackend(skillId: string): FilesBackend {
  return {
    kind: 'vfs',
    ownerId: skillId,
    capabilities: {
      // `folders: true` = the user can create folders. Like VFS, the storage
      // has no folder model — `useFolders` tracks them client-side.
      folders: true,
      indexStatus: false,
      search: false,
      copy: false,
      // Skills are small/bounded — keep the fetch-all `useEntries` path.
      lazyChildren: false,
      recursiveList: false,
    },

    useEntries(): UseEntriesResult {
      const [entries, setEntries] = useState<FilesEntry[]>(() =>
        getSkillFilesSnapshot(skillId),
      );
      const [isLoading, setIsLoading] = useState(true);
      const [error, setError] = useState<Error | null>(null);

      // Mirror store mutations into local state.
      useEffect(() => {
        const unsub = subscribeSkillFiles(skillId, () => {
          setEntries(getSkillFilesSnapshot(skillId));
        });
        // Pick up any entries a sibling instance already loaded.
        setEntries(getSkillFilesSnapshot(skillId));
        return unsub;
      }, []);

      const refetch = useCallback(async (): Promise<void> => {
        try {
          const refs = await skillFilesApi.list(skillId);
          setAllSkillFiles(skillId, refs);
          setError(null);
        } catch (err) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }, []);

      // Cold-load on mount.
      useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        refetch().finally(() => {
          if (!cancelled) setIsLoading(false);
        });
        return () => {
          cancelled = true;
        };
      }, [refetch]);

      return { entries, isLoading, error, refetch };
    },

    useUpload(): UseUploadResult {
      const [jobs, setJobs] = useState<VfsUploadJob[]>([]);

      const updateJob = useCallback(
        (path: string, patch: Partial<VfsUploadJob>) => {
          setJobs((prev) => prev.map((j) => (j.path === path ? { ...j, ...patch } : j)));
        },
        [],
      );

      const uploadFile = useCallback(
        async (
          file: File,
          opts?: { prefix?: string; name?: string },
        ): Promise<VfsEntry | null> => {
          const path = joinVfsPath(opts?.prefix, opts?.name ?? file.name);
          setJobs((prev) => [
            ...prev.filter((j) => j.path !== path),
            { file, path, progress: 0, status: 'pending' },
          ]);

          if (file.size > MAX_FILE_BYTES) {
            updateJob(path, { status: 'error', error: 'File exceeds the 10 MB limit' });
            return null;
          }

          try {
            updateJob(path, { status: 'uploading' });
            const data = await fileToBase64(file);
            const ref = await skillFilesApi.upload(skillId, {
              path,
              contentType: file.type || 'application/octet-stream',
              data,
            });
            upsertSkillFile(skillId, ref);
            // The path is real on the server now — drop any virtual folder it
            // landed inside (same as the VFS adapter).
            pruneRealisedFolders(skillId, ref.path);
            const entry = refToEntry(skillId, ref);
            updateJob(path, { status: 'done', progress: 1, result: entry });
            return entry;
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            updateJob(path, { status: 'error', error: message });
            return null;
          }
        },
        [updateJob],
      );

      // Base64 POST has no abort hook — cancel just drops the job row.
      const cancel = useCallback((path: string) => {
        setJobs((prev) => prev.filter((j) => j.path !== path));
      }, []);

      const clearDone = useCallback(() => {
        setJobs((prev) => prev.filter((j) => j.status !== 'done'));
      }, []);

      return { jobs, uploadFile, cancel, clearDone };
    },

    useDelete(): UseDeleteResult {
      const [isDeleting, setIsDeleting] = useState(false);
      const [error, setError] = useState<Error | null>(null);

      const deleteFile = useCallback(async (path: string): Promise<boolean> => {
        setIsDeleting(true);
        setError(null);
        try {
          await skillFilesApi.delete(skillId, path);
          removeSkillFile(skillId, path);
          return true;
        } catch (err) {
          setError(err instanceof Error ? err : new Error(String(err)));
          return false;
        } finally {
          setIsDeleting(false);
        }
      }, []);

      const deleteFolder = useCallback(
        async (prefix: string): Promise<number | null> => {
          setIsDeleting(true);
          setError(null);
          try {
            const { deletedCount } = await skillFilesApi.delete(skillId, prefix);
            removeSkillFilePrefix(skillId, prefix);
            return deletedCount;
          } catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
            return null;
          } finally {
            setIsDeleting(false);
          }
        },
        [],
      );

      return { deleteFile, deleteFolder, isDeleting, error };
    },

    useFolders(): UseFoldersResult {
      const [folders, setFolders] = useState<string[]>(() =>
        Array.from(getVirtualFolders(skillId)),
      );
      useEffect(() => {
        const unsub = subscribeVirtualFolders(skillId, () => {
          setFolders(Array.from(getVirtualFolders(skillId)));
        });
        return unsub;
      }, []);
      const [isCreating, setIsCreating] = useState(false);
      const mkdir = useCallback(async ({ path }: { path: string }) => {
        setIsCreating(true);
        try {
          addVirtualFolder(skillId, path);
        } finally {
          setIsCreating(false);
        }
      }, []);
      return { folders, mkdir, isCreating };
    },

    async move({ from, to, overwrite }): Promise<VfsEntry> {
      // A 409 (destination exists) throws an ApiError with `status: 409`,
      // which AuiFiles' `doMove` catches and surfaces as a toast.
      const ref = await skillFilesApi.move(skillId, { from, to, overwrite });
      moveSkillFile(skillId, from, to);
      return refToEntry(skillId, ref);
    },

    async getDownloadUrl(path) {
      const { url } = await skillFilesApi.getDownloadUrl(skillId, path);
      return url;
    },

    async readText(path) {
      // Two hops: presigned URL → fetch the bytes (no cookie needed for S3).
      const { url } = await skillFilesApi.getDownloadUrl(skillId, path);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      return res.text();
    },
  };
}
